import React, { useCallback, useEffect, useState } from 'react';
import styles from './WorkWithRadialToBarchart.module.scss';
import { PortfolioSimpleIndicatorTable } from '../PortfolioSimpleIndicatorTable/PortfolioSimpleIndicatorTable';
import { ProjectCounter } from '../ProjectCounter/ProjectCounter';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import { getProjectsByFundingSource } from '../../../redux/actions/indicatorProjects';
import { useDispatch, useSelector } from 'react-redux';
import Indicator from '../../../models/Indicator';
import { handleLoading } from '../../utils';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { useTranslation } from 'react-i18next';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import { buildTransactionalResourcesDatasetFromProjects } from '../FiltersInCharts/TransactionalResourcesFilterChart';

export const WorkWithRadialToBarchart = () => {
  const currentFilter = localStorage.getItem('transactionalSupervisionResourcesFilter');
  const currentIndicator = useSelector<any, Indicator>(state => state.currentIndicator);
  const { id } = currentIndicator;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const projects = useSelector<any, any>(state => state.indicatorProjects);
  const [loadingData, setLoadingData] = useState<boolean>(false);

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [flag, setFlag] = useState<boolean>(false);

  const handleFilteredProjectsChange = useCallback(
    filteredProjects => {
      const currentDataset = currentIndicator?.data?.toBarchart?.data?.datasets?.[0];
      if (!currentDataset) {
        return;
      }

      const chartData = buildTransactionalResourcesDatasetFromProjects(filteredProjects);
      if (
        Array.isArray(currentDataset.data) &&
        currentDataset.data.length === chartData.length &&
        currentDataset.data.every((value, index) => value === chartData[index])
      ) {
        return;
      }

      dispatch(
        setCurrentIndicator({
          ...currentIndicator,
          data: {
            ...currentIndicator.data,
            toBarchart: {
              ...currentIndicator.data.toBarchart,
              data: {
                ...currentIndicator.data.toBarchart.data,
                datasets: [
                  {
                    ...currentDataset,
                    data: chartData,
                  },
                  ...currentIndicator.data.toBarchart.data.datasets.slice(1),
                ],
              },
            },
          },
        }),
      );
    },
    [currentIndicator, dispatch],
  );

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));

    try {
      const indicatorsToProcess = await getNewFilteredIndicators();
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'SUPERVISION_PLAN_BUDGET_STATUS')));
  };

  useEffect(() => {
    getFilteredProjects(dispatch, id, currentFilter, setLoadingData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilter, filterIndicators.common]);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();

    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  return (
    <>
      <div className={styles.workWithRadialToBarchartContainer}>
        <PortfolioSimpleIndicatorTable
          projects={projects}
          tableInfo={tableFormat}
          indicatorKey={IndicatorKeyEnum.SUPERVISION_PLAN_BUDGET_STATUS}
          loading={loadingData}
          onFilteredProjectsChange={handleFilteredProjectsChange}
        />
        <ProjectCounter
          arrayToCount={projects}
          countProjects={true}
          indicatorKey={IndicatorKeyEnum.SUPERVISION_PLAN_BUDGET_STATUS}
        />
      </div>
    </>
  );
};

const tableFormat = [
  {
    tableHeader: 'spent_usd',
    rootProperty: 'field1',
    fieldType: 'money',
    customWidth: { minWidth: '140px' },
  },
  {
    tableHeader: 'committed_supervision_usd',
    rootProperty: 'field2',
    fieldType: 'money',
    customWidth: { minWidth: '140px' },
  },
  {
    tableHeader: 'balance',
    rootProperty: 'field4',
    fieldType: 'money',
    customWidth: { minWidth: '140px' },
  },
];

const getFilteredProjects = (dispatch, currentIndicatorId, fundingSource, setLoadingData) => {
  return handleLoading(() => dispatch(getProjectsByFundingSource(fundingSource, currentIndicatorId)), setLoadingData);
};
